import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { rememberLanguage } from "@/i18n";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Search, Globe, Menu, X } from "lucide-react";
import { Brand } from "@/components/brand";
import { creatorsListQuery, aggregatesQuery, countriesFrom, allVideosQuery, videoParam } from "@/lib/queries";
import { norm } from "@/lib/archive";
import { translateCountry } from "@/lib/format";
import "@/i18n";

export function SiteNav() {
  const { t, i18n } = useTranslation();
  const [langOpen, setLangOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Close the mobile drawer whenever the route changes.
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  const langs: Array<{ code: "ca" | "es" | "en"; label: string }> = [
    { code: "ca", label: "CA" },
    { code: "es", label: "ES" },
    { code: "en", label: "EN" },
  ];
  const current = (i18n.resolvedLanguage ?? "es").slice(0, 2).toUpperCase();

  const links: Array<{
    to: "/" | "/youtubers" | "/videos" | "/countries" | "/about";
    label: string;
    exact?: boolean;
  }> = [
    { to: "/", label: t("nav.atlas"), exact: true },
    { to: "/youtubers", label: t("nav.explorers") },
    { to: "/videos", label: t("nav.search") },
    { to: "/countries", label: t("nav.countries") },
    
  ];

  return (
    <nav className="sticky top-0 z-40 bg-paper/85 backdrop-blur-md border-b border-ink/8">
      <div className="max-w-7xl mx-auto px-6 h-20 grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 md:flex md:justify-between md:gap-6">
        <div className="flex items-center gap-10 min-w-0">
          <Brand size="md" />

          <div className="hidden md:flex gap-7 text-sm font-medium text-ink/60">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                activeProps={{ className: "text-ink" }}
                {...(l.exact ? { activeOptions: { exact: true } } : {})}
                className="hover:text-ink transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2 md:gap-3 shrink-0">
          <NavSearch className="hidden sm:block" />
          <div className="relative">
            <button
              onClick={() => setLangOpen((v) => !v)}
              onBlur={() => setTimeout(() => setLangOpen(false), 150)}
              className="flex items-center gap-1.5 text-mono-caps text-ink/60 hover:text-ink transition-colors px-2 py-1"
              aria-label={t("common.changeLanguage")}
            >
              <Globe className="size-3.5" />
              {current}
            </button>
            {langOpen && (
              <div className="absolute right-0 top-full mt-1 bg-paper ring-hairline shadow-lg min-w-[80px] z-50">
                {langs.map((l) => (
                  <button
                    key={l.code}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      i18n.changeLanguage(l.code);
                      rememberLanguage(l.code);
                      setLangOpen(false);
                    }}
                    className={`block w-full text-left px-3 py-1.5 text-mono-caps hover:bg-secondary ${
                      current === l.label ? "text-amber" : "text-ink/70"
                    }`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? t("common.closeMenu") : t("common.openMenu")}
            aria-expanded={menuOpen}
            className="md:hidden inline-flex min-h-11 min-w-11 items-center justify-center text-ink/70 hover:text-ink"
          >
            {menuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-ink/8 bg-paper px-6 py-4 space-y-4">
          <NavSearch className="block" fullWidth onNavigate={() => setMenuOpen(false)} />
          <div className="flex flex-col">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                activeProps={{ className: "text-ink" }}
                {...(l.exact ? { activeOptions: { exact: true } } : {})}
                onClick={() => setMenuOpen(false)}
                className="py-3 text-base font-medium text-ink/60 hover:text-ink transition-colors border-b border-ink/8 last:border-0"
              >
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}

function NavSearch({
  className = "",
  fullWidth = false,
  onNavigate,
}: {
  className?: string;
  fullWidth?: boolean;
  onNavigate?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);

  const { data: creators = [] } = useQuery(creatorsListQuery);
  const { data: agg = [] } = useQuery(aggregatesQuery);
  const countries = useMemo(() => countriesFrom(agg), [agg]);
  const [wantVideos, setWantVideos] = useState(false);
  useEffect(() => { if (open) setWantVideos(true); }, [open]);
  const { data: allVideos = [] } = useQuery({ ...allVideosQuery, enabled: wantVideos });
  const videoIndex = useMemo(
    () => allVideos.map((v) => ({ v, key: norm(`${v.title} ${v.youtubers?.name ?? ""} ${v.video_locations.map((l) => `${l.city ?? ""} ${l.name ?? ""}`).join(" ")}`) })),
    [allVideos],
  );

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const query = q.trim().toLowerCase();
  const matches = useMemo(() => {
    if (!query) return [] as Array<{ kind: "y" | "c" | "v"; id: string; label: string; sub?: string; go: () => void }>;
    const yy = creators
      .filter((c) => {
        if (c.name.toLowerCase().includes(query)) return true;
        if (c.full_name && c.full_name.toLowerCase().includes(query)) return true;
        if (c.slug.toLowerCase().includes(query)) return true;
        return c.channels.some(
          (ch) => ch.name.toLowerCase().includes(query) || ch.handle.toLowerCase().includes(query),
        );
      })
      .slice(0, 5)
      .map((c) => {
        const primary = c.channels.find((ch) => ch.is_primary_channel) ?? c.channels[0];
        return {
          kind: "y" as const,
          id: `y-${c.id}`,
          label: c.full_name || c.name,
          sub: primary ? `@${primary.handle}` : c.slug,
          go: () => navigate({ to: "/y/$handle", params: { handle: c.slug } }),
        };
      });
    const cc = countries
      .map((c) => ({ ...c, tName: translateCountry(c.code, c.name, i18n.language) }))
      .filter((c) => c.tName.toLowerCase().includes(query) || c.code.toLowerCase().includes(query))
      .slice(0, 6)
      .map((c) => ({
        kind: "c" as const,
        id: `c-${c.code}`,
        label: c.tName,
        sub: `${c.videos} ${t("common.videos")}`,
        go: () => navigate({ to: "/country/$code", params: { code: c.code.toLowerCase() } }),
      }));
    const terms = norm(query).split(/\s+/).filter(Boolean);
    const vv = [] as Array<{ kind: "v"; id: string; label: string; sub?: string; go: () => void }>;
    for (const { v, key } of videoIndex) {
      if (!terms.every((w) => key.includes(w))) continue;
      vv.push({
        kind: "v",
        id: `v-${v.id}`,
        label: v.title,
        sub: v.youtubers?.name ?? "",
        go: () => navigate({ to: "/v/$slug", params: { slug: videoParam(v) } }),
      });
      if (vv.length >= 8) break;
    }
    return [...yy, ...cc, ...vv];
  }, [query, creators, countries, videoIndex, i18n.language, navigate, t]);

  const submit = () => {
    if (matches[active]) {
      matches[active].go();
    } else if (q.trim()) {
      navigate({ to: "/videos", search: { q: q.trim() } });
    }
    setOpen(false);
    setQ("");
    onNavigate?.();
  };

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <div className="flex items-center gap-2 bg-secondary text-sm px-3 py-1.5 rounded-full focus-within:ring-1 focus-within:ring-amber">
        <Search className="size-3.5 text-ink/50" />
        <input
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
            setActive(0);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setActive((i) => Math.min(matches.length - 1, i + 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setActive((i) => Math.max(0, i - 1));
            } else if (e.key === "Enter") {
              e.preventDefault();
              submit();
            } else if (e.key === "Escape") {
              setOpen(false);
            }
          }}
          placeholder={t("nav.searchPlaceholder")}
          className={`bg-transparent outline-none text-sm placeholder:text-ink/50 ${
            fullWidth ? "w-full" : "w-40 md:w-56"
          }`}
        />
      </div>
      {open && matches.length > 0 && (
        <div className="absolute left-0 right-0 sm:left-auto top-full mt-1 bg-paper ring-hairline shadow-lg min-w-[280px] z-50 max-h-[70vh] overflow-y-auto">
          {matches.some((m) => m.kind === "y") && (
            <div className="px-3 pt-2 pb-1 text-mono-caps text-ink/40">
              {t("nav.youtubers")}
            </div>
          )}
          {matches.filter((m) => m.kind === "y").map((m) => {
            const i = matches.indexOf(m);
            return (
              <button
                key={m.id}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  m.go();
                  setOpen(false);
                  setQ("");
                  onNavigate?.();
                }}
                className={`w-full text-left px-3 py-2 flex items-center justify-between ${
                  active === i ? "bg-secondary" : "hover:bg-secondary/60"
                }`}
              >
                <span className="text-sm font-serif">{m.label}</span>
                <span className="text-[10px] font-mono text-ink/50">{m.sub}</span>
              </button>
            );
          })}
          {matches.some((m) => m.kind === "c") && (
            <div className="px-3 pt-2 pb-1 text-mono-caps text-ink/40 border-t border-ink/8">
              {t("nav.countries")}
            </div>
          )}
          {matches.filter((m) => m.kind === "c").map((m) => {
            const i = matches.indexOf(m);
            const code = m.id.slice(2).toLowerCase();
            return (
              <button
                key={m.id}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  m.go();
                  setOpen(false);
                  setQ("");
                  onNavigate?.();
                }}
                className={`w-full text-left px-3 py-2 flex items-center justify-between gap-2 ${
                  active === i ? "bg-secondary" : "hover:bg-secondary/60"
                }`}
              >
                <span className="flex items-center gap-2 text-sm">
                  <img
                    src={`https://flagcdn.com/w20/${code}.png`}
                    alt=""
                    width={16}
                    height={12}
                    className="ring-1 ring-ink/10"
                  />
                  {m.label}
                </span>
                <span className="text-[10px] font-mono text-ink/50">{m.sub}</span>
              </button>
            );
          })}
          {matches.some((m) => m.kind === "v") && (
            <div className="px-3 pt-2 pb-1 text-mono-caps text-ink/40 border-t border-ink/8">
              {t("nav.search")}
            </div>
          )}
          {matches.filter((m) => m.kind === "v").map((m) => {
            const i = matches.indexOf(m);
            return (
              <button
                key={m.id}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  m.go();
                  setOpen(false);
                  setQ("");
                  onNavigate?.();
                }}
                className={`w-full text-left px-3 py-2 flex items-center justify-between gap-3 ${
                  active === i ? "bg-secondary" : "hover:bg-secondary/60"
                }`}
              >
                <span className="text-sm line-clamp-1">{m.label}</span>
                <span className="text-[10px] font-mono text-ink/50 shrink-0">{m.sub}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
